import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import authService from "../../services/authService";
import { validateAuthSession } from "../../utils/authSession";
import {
  clearAuthRedirectMark,
  isAuthRedirectLooping,
  markAuthRedirect,
  prefersUserGestureSignIn,
} from "../../utils/authRedirectGuard";

import FullscreenLoadingOverlay from "../../components/FullscreenLoadingOverlay";

function LoginUI() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [message, setMessage] = useState(() => t("auth.checkingSecurity"));
  const [needsManualSignIn, setNeedsManualSignIn] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const startSignIn = () => {
    markAuthRedirect();
    setSubmitting(true);
    void Promise.resolve(authService.login()).catch((error) => {
      console.error("Sign-in redirect failed:", error);
      setSubmitting(false);
      setNeedsManualSignIn(true);
    });
  };

  useEffect(() => {
    let cancelled = false;

    const checkAuthAndRedirect = async () => {
      try {
        const session = await validateAuthSession(undefined, { renew: false });

        if (cancelled) {
          return;
        }

        if (session.valid && session.user) {
          clearAuthRedirectMark();
          setMessage(t("auth.redirecting"));
          navigate("/dashboard", { replace: true });
          return;
        }

        if (prefersUserGestureSignIn() || isAuthRedirectLooping()) {
          setNeedsManualSignIn(true);
          return;
        }

        startSignIn();
      } catch (error) {
        console.error("Authentication check failed:", error);
        if (cancelled) {
          return;
        }

        if (prefersUserGestureSignIn() || isAuthRedirectLooping()) {
          setNeedsManualSignIn(true);
          return;
        }

        startSignIn();
      }
    };

    void checkAuthAndRedirect();

    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted || cancelled) {
        return;
      }
      setSubmitting(false);
      setNeedsManualSignIn(false);
      void checkAuthAndRedirect();
    };

    window.addEventListener("pageshow", onPageShow);

    return () => {
      cancelled = true;
      window.removeEventListener("pageshow", onPageShow);
    };
    // startSignIn is stable enough for this mount; t is read when the check finishes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  if (needsManualSignIn) {
    return (
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: (theme) => theme.zIndex.modal + 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 3,
          backgroundColor: "background.default",
        }}
      >
        <Stack spacing={2} sx={{ maxWidth: 360, width: "100%", textAlign: "center" }}>
          <Typography variant="h6">{t("auth.signInTitle")}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t("auth.signInBody")}
          </Typography>
          <Button variant="contained" disabled={submitting} onClick={startSignIn}>
            {t("auth.signIn")}
          </Button>
        </Stack>
      </Box>
    );
  }

  return <FullscreenLoadingOverlay message={message} />;
}

export default LoginUI;
